import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import TopBar from './components/TopBar';
import { useAuth, type Ciudadano } from './stores/auth';
import { api } from './api/client';
import { useStaffAuth } from './stores/staffAuth';
import Login from './pages/Login';
import LoginStaff from './pages/LoginStaff';
import StaffHome from './pages/staff/StaffHome';
import StaffReclamos from './pages/staff/StaffReclamos';
import StaffDetalleReclamo from './pages/staff/StaffDetalleReclamo';
import Inicio from './pages/Inicio';
import Estacionar from './pages/Estacionar';
import Billetera from './pages/Billetera';
import Infracciones from './pages/Infracciones';
import Reclamos from './pages/Reclamos';
import NuevoReclamo from './pages/NuevoReclamo';
import DetalleReclamo from './pages/DetalleReclamo';
import Perfil from './pages/Perfil';
import CambiarPassword from './pages/CambiarPassword';
import Avisos from './pages/Avisos';
import DetalleAviso from './pages/DetalleAviso';
import Obras from './pages/Obras';
import DetalleObra from './pages/DetalleObra';
import Tramites from './pages/Tramites';
import Encuestas from './pages/Encuestas';
import Clima from './pages/Clima';
import Register from './pages/Register';
import EditarDatos from './pages/EditarDatos';
import MisImpuestos from './pages/MisImpuestos';
import EmergenciaPolicial from './pages/EmergenciaPolicial';
import Comercios from './pages/Comercios';
import DetalleComercio from './pages/DetalleComercio';
import MapaPOI from './pages/MapaPOI';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuth((s) => s.accessToken);
  const loc = useLocation();
  if (!token) return <Navigate to="/login" state={{ from: loc }} replace />;
  return <>{children}</>;
}

function RequireStaffAuth({ children }: { children: React.ReactNode }) {
  const token = useStaffAuth((s) => s.accessToken);
  if (!token) return <Navigate to="/login/staff" replace />;
  return <>{children}</>;
}

function RequireVerified({ children }: { children: React.ReactNode }) {
  const nav       = useNavigate();
  const ciudadano = useAuth((s) => s.ciudadano);
  const noVerificado = ciudadano?.identidad_verificada === 0 || ciudadano?.identidad_verificada === false;
  if (noVerificado) {
    return (
      <>
        <TopBar title="Acceso restringido" back />
        <main className="max-w-sm mx-auto px-5 py-10 flex flex-col items-center text-center space-y-4">
          <span className="text-6xl">🔒</span>
          <h2 className="text-xl font-extrabold text-gray-900">Verificá tu identidad</h2>
          <p className="text-gray-500 text-sm leading-relaxed">
            Para acceder a esta sección necesitás que un agente municipal verifique tu cuenta.
          </p>
          <button
            onClick={() => nav('/')}
            className="w-full bg-brand-600 text-white rounded-xl py-3.5 font-bold text-base mt-2"
          >
            Volver al inicio
          </button>
        </main>
      </>
    );
  }
  return <>{children}</>;
}

function useRefreshCiudadano() {
  const token       = useAuth((s) => s.accessToken);
  const setCiudadano = useAuth((s) => s.setCiudadano);

  useEffect(() => {
    if (!token) return;

    const refresh = async () => {
      try {
        const data = await api<Ciudadano>('/auth?action=me');
        if (data) setCiudadano(data);
      } catch { /* silencioso */ }
    };

    refresh();

    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);

    // Push en vivo: el SW reenvía los push a las pestañas abiertas. Cuando el
    // admin verifica la identidad, el push trae { refresh: 'ciudadano' } → re-
    // consultamos /auth?me y el store se actualiza al toque (desbloquea las
    // funciones sin que el usuario tenga que refrescar la app).
    const onSwMessage = (e: MessageEvent) => {
      if (e.data?.refresh === 'ciudadano') refresh();
    };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
    };
  }, [token]);
}

export default function App() {
  useRefreshCiudadano();
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/login"        element={<Login />} />
      <Route path="/login/staff"  element={<LoginStaff />} />
      <Route path="/registro"     element={<Register />} />
      <Route path="/staff"              element={<RequireStaffAuth><StaffHome /></RequireStaffAuth>} />
      <Route path="/staff/reclamos"     element={<RequireStaffAuth><StaffReclamos /></RequireStaffAuth>} />
      <Route path="/staff/reclamos/:id" element={<RequireStaffAuth><StaffDetalleReclamo /></RequireStaffAuth>} />
      <Route path="/"             element={<RequireAuth><Inicio /></RequireAuth>} />
      <Route path="/mis-impuestos"  element={<RequireAuth><RequireVerified><MisImpuestos /></RequireVerified></RequireAuth>} />
      <Route path="/estacionar"   element={<RequireAuth><RequireVerified><Estacionar /></RequireVerified></RequireAuth>} />
      <Route path="/billetera"    element={<RequireAuth><RequireVerified><Billetera /></RequireVerified></RequireAuth>} />
      <Route path="/infracciones" element={<RequireAuth><RequireVerified><Infracciones /></RequireVerified></RequireAuth>} />
      <Route path="/reclamos"     element={<RequireAuth><Reclamos /></RequireAuth>} />
      <Route path="/reclamos/nuevo" element={<RequireAuth><NuevoReclamo /></RequireAuth>} />
      <Route path="/reclamos/:id" element={<RequireAuth><DetalleReclamo /></RequireAuth>} />
      <Route path="/perfil"            element={<RequireAuth><Perfil /></RequireAuth>} />
      <Route path="/cambiar-password" element={<RequireAuth><CambiarPassword /></RequireAuth>} />
      <Route path="/editar-datos"    element={<RequireAuth><EditarDatos /></RequireAuth>} />
      <Route path="/emergencia"      element={<RequireAuth><EmergenciaPolicial /></RequireAuth>} />
      <Route path="/avisos"           element={<RequireAuth><Avisos /></RequireAuth>} />
      <Route path="/avisos/:id"       element={<RequireAuth><DetalleAviso /></RequireAuth>} />
      <Route path="/obras"            element={<RequireAuth><Obras /></RequireAuth>} />
      <Route path="/obras/:id"        element={<RequireAuth><DetalleObra /></RequireAuth>} />
      <Route path="/tramites"         element={<RequireAuth><Tramites /></RequireAuth>} />
      <Route path="/encuestas"        element={<RequireAuth><Encuestas /></RequireAuth>} />
      <Route path="/clima"            element={<RequireAuth><Clima /></RequireAuth>} />
      <Route path="/comercios"        element={<RequireAuth><Comercios /></RequireAuth>} />
      <Route path="/comercios/:id"    element={<RequireAuth><DetalleComercio /></RequireAuth>} />
      <Route path="/mapa-poi"         element={<RequireAuth><MapaPOI /></RequireAuth>} />
      <Route path="*"                 element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
